
import { Component, Vue } from 'vue-property-decorator'
import { MonitorService } from '@/services/index'
import { ITrendDataRaw } from '@/types/monitor-types'

const HOUR = 3600 * 1000
const DAY = 24 * HOUR
const TOTAL_MAX = 26000
const WD = ['日', '一', '二', '三', '四', '五', '六']

type TrendRange = '24h' | '3d' | '7d' | '30d'

interface ITrendPoint {
    /** 桶起始时间戳（ms） */
    ts: number
    human: number
    bot: number
    unk: number
    total: number
}

interface ITrendData {
    range: TrendRange
    bucketH: number
    unit: string
    points: ITrendPoint[]
    peakIdx: number
    generatedAt: string
}

/** 档位 → 显示标签 */
const RANGE_LABELS: Record<TrendRange, string> = {
    '24h': '实时',
    '3d':  '3 天',
    '7d':  '7 天',
    '30d': '30 天'
}
/** 档位 → 桶跨度（小时） */
const RANGE_BUCKET: Record<TrendRange, number> = {
    '24h': 1,
    '3d':  3,
    '7d':  6,
    '30d': 24
}
const RANGE_ORDER: TrendRange[] = ['24h', '3d', '7d', '30d']

/** 桶跨度 → 文案（如 1 →「1 小时」、24 →「1 天」） */
function bucketUnit(bucketH: number): string {
    if (bucketH >= 24) return '1 天'
    if (bucketH === 3) return '3 小时'
    if (bucketH === 6) return '6 小时'
    return bucketH + ' 小时'
}

/** 后端 ts 字符串 yyyy-MM-dd HH:mm → 本地时间戳（避免 Safari 解析「-」格式失败） */
function parseTs(str: string): number {
    return new Date(str.replace(/-/g, '/')).getTime()
}

/** 后端响应 → 前端渲染模型 */
function mapTrend(resp: ITrendDataRaw): ITrendData {
    const points: ITrendPoint[] = resp.points.map(p => ({
        ts: parseTs(p.ts),
        human: p.human,
        bot: p.bot,
        unk: p.unk,
        total: p.total
    }))
    // 桶跨度：约定字段为 bucket_hours（snake_case）；部分实现可能误用驼峰 bucketHours，
    // 两者都尝试读取；若都缺失则从相邻点 ts 差兜底，再退化到档位默认。
    // 任何分支都得到合法 number，从而杜绝 hint 出现「undefined 小时/桶」。
    // 注意：此处刻意不用 ?? 运算符——本项目 babel 配置未启用 nullish-coalescing 插件，
    // 会导致编译期 SyntaxError，故改用显式三元兼容旧 parser。
    const rawBucket = resp.bucket_hours !== undefined
        ? resp.bucket_hours
        : (resp as Record<string, unknown>).bucketHours
    let bucketH: number = rawBucket as number
    if (typeof bucketH !== 'number' || !(bucketH > 0)) {
        bucketH = points.length > 1
            ? Math.round((points[1].ts - points[0].ts) / HOUR)
            : (RANGE_BUCKET[resp.range as TrendRange] || 1)
    }
    // 峰值：优先用后端 peak 字段（接口文档要求直接采用，不必自己扫数组）
    let peakIdx = -1
    if (resp.peak && resp.peak.ts) {
        const pkTs = parseTs(resp.peak.ts)
        peakIdx = points.findIndex(p => p.ts === pkTs)
    }
    // 兜底：peak.ts 未命中（理论上不会）时自行扫描
    if (peakIdx < 0) {
        for (let i = 1; i < points.length; i++) {
            if (points[i].total > points[peakIdx < 0 ? 0 : peakIdx].total) peakIdx = i
        }
    }
    return {
        range: resp.range,
        bucketH,
        unit: bucketUnit(bucketH),
        points,
        peakIdx,
        generatedAt: resp.generated_at
    }
}

/** 空占位（初始 / 无数据）：避免首屏空白闪烁 */
function emptyTrend(range: TrendRange): ITrendData {
    const bucketH = RANGE_BUCKET[range]
    return {
        range,
        bucketH,
        unit: bucketUnit(bucketH),
        points: [],
        peakIdx: -1,
        generatedAt: ''
    }
}

@Component({ name: 'DashboardHourlyChart' })
export default class DashboardHourlyChart extends Vue {
    /** 当前档位（默认实时） */
    private curRange: TrendRange = '24h'
    /** 各档数据缓存，避免切换重算抖动 */
    private cache: Partial<Record<TrendRange, ITrendData>> = {}
    /** 当前渲染数据 */
    private curData: ITrendData = emptyTrend('24h')
    /** 是否正在拉取（首屏 / 无缓存档位显示加载态） */
    private loading = false

    /** SVG 宽高 - 跟随 chart-area 实际尺寸 */
    private svgW: number = 800
    private svgH: number = 252

    /** 当前悬停的节点索引（-1 表示未悬停） */
    private hoverIdx: number = -1

    /** resize 防抖 */
    private rzTimer: number | null = null

    /** Y 轴 4 档（0 / 6.5k / 13k / 19.5k / 26k） */
    private gridLines: Array<{ top: number, label: string }> = [
        { top: 0,   label: '26k' },
        { top: 25,  label: '19.5k' },
        { top: 50,  label: '13k' },
        { top: 75,  label: '6.5k' }
    ]

    /** 档位切换器数据 */
    private get rangeList() {
        return RANGE_ORDER.map(k => ({ key: k, label: RANGE_LABELS[k] }))
    }

    /**
     * 右上角提示：随档位变化
     * 强调「节点值 = 每小时均值」—— 3天/7天档下用户易把 `3 小时/桶` 误读成区间总量，
     * 这与设计文档 §7.3 核心口径一致（节点值不是区间总量，避免切档时 Y 轴数量级跳变）。
     */
    private get hintText(): string {
        const n = this.curData.points.length || RANGE_BUCKET[this.curRange]
        const u = this.curData.unit
        return `次/小时 · ${u}/桶 · ${n} 节点 · 节点值=小时均值`
    }

    private mounted() {
        this.recalcSize()
        this.loadTrend('24h')
        window.addEventListener('resize', this.handleResize, true)
    }

    private beforeDestroy() {
        window.removeEventListener('resize', this.handleResize, true)
        if (this.rzTimer !== null) {
            window.clearTimeout(this.rzTimer)
            this.rzTimer = null
        }
    }

    /** 切换档位（切档时调用一次，不轮询；有缓存先即时渲染再后台刷新） */
    private setRange(key: TrendRange) {
        if (key === this.curRange) return
        this.curRange = key
        this.loadTrend(key)
    }

    /** 拉取趋势数据：GET /v1/monitor/trend?range= */
    private async loadTrend(key: TrendRange) {
        if (this.cache[key]) {
            // 已有缓存：即时渲染，随后仍请求最新（后端已有缓存，开销极低）
            this.curData = this.cache[key]!
            this.hoverIdx = -1
            this.$nextTick(() => this.playDraw())
        } else {
            this.loading = true
        }
        try {
            const res = await MonitorService.getTrend(key)
            if (res.status === 200 && res.data) {
                const data = mapTrend(res.data as ITrendDataRaw)
                this.cache[key] = data
                this.curData = data
                this.hoverIdx = -1
                this.$nextTick(() => this.playDraw())
            }
            // status != 200 或 data=null：保留当前值（无数据兜底，不闪烁）
        } catch (e) {
            // 网络异常由 xhr/common-handler 统一提示，此处静默保留当前值
        } finally {
            this.loading = false
        }
    }

    /**
     * 描边生长动画（对齐原型 drawLine(animate)）
     * 三条组件线依次 stroke-dashoffset 描边（每条延迟 k*0.16s），总量主线最后压轴（1.05s + 延迟 0.5s）
     */
    private playDraw() {
        const totalRef = this.$refs.pTotal as SVGPathElement | undefined
        if (!totalRef) return
        const subRefs = [this.$refs.pHuman, this.$refs.pBot, this.$refs.pUnk]
            .filter(Boolean) as SVGPathElement[]
        const all = [...subRefs, totalRef]
        const isDash = (el: SVGElement) =>
            el.classList.contains('line-sub--dash') ? '4 3' : ''

        // 尊重「减少动态效果」系统偏好：直接显示，不做描边动画
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            all.forEach(el => {
                el.style.strokeDasharray = isDash(el)
                el.style.strokeDashoffset = ''
                el.style.transition = ''
            })
            return
        }

        // 先复位，避免上一次动画的 inline 残留（Vue 不会清掉 JS 写的 style）
        all.forEach(el => {
            el.style.transition = ''
            el.style.strokeDashoffset = ''
            el.style.strokeDasharray = ''
        })
        // 强制回流，让复位生效后再设置初始状态
        void totalRef.getBoundingClientRect()

        all.forEach(el => {
            try {
                const L = el.getTotalLength()
                el.style.strokeDasharray = String(L)
                el.style.strokeDashoffset = String(L)
            } catch (e) { /* 隐藏元素上可能抛错，忽略 */ }
        })
        void totalRef.getBoundingClientRect()

        subRefs.forEach((el, k) => {
            el.style.transition = `stroke-dashoffset .7s cubic-bezier(.3,.6,.2,1) ${k * 0.16}s`
            el.style.strokeDashoffset = '0'
            el.addEventListener('transitionend', () => {
                el.style.strokeDasharray = isDash(el)
                el.style.transition = ''
            }, { once: true })
        })

        totalRef.style.transition = 'stroke-dashoffset 1.05s cubic-bezier(.3,.6,.2,1) .5s'
        totalRef.style.strokeDashoffset = '0'
        totalRef.addEventListener('transitionend', () => {
            totalRef.style.strokeDasharray = ''
            totalRef.style.transition = ''
        }, { once: true })

        // 兜底：防止 transitionend 未触发（如被中断）导致线条被留在隐藏态
        window.setTimeout(() => {
            all.forEach(el => {
                el.style.strokeDasharray = isDash(el)
                el.style.strokeDashoffset = ''
                el.style.transition = ''
            })
        }, 1800)
    }

    /** 读取容器实际宽高 */
    private recalcSize() {
        const el = this.$refs.chartArea as HTMLElement | undefined
        if (!el) return
        const w = el.clientWidth - 48 - 2
        if (w > 0 && w !== this.svgW) {
            this.svgW = w
        }
    }

    private handleResize = () => {
        if (this.rzTimer !== null) {
            window.clearTimeout(this.rzTimer)
        }
        this.rzTimer = window.setTimeout(() => {
            this.recalcSize()
        }, 180)
    }

    /** 安全取点：索引越界或数据未就绪时返回全 0 对象 */
    private pointAt(i: number): ITrendPoint {
        const p = this.curData.points[i]
        return p || { ts: 0, human: 0, bot: 0, unk: 0, total: 0 }
    }

    /** 计算布局：bw 单格宽，gap 间距 */
    private get layout(): { bw: number, gap: number } {
        const gap = 4
        const n = this.curData.points.length
        const bw = n > 0 ? (this.svgW - (n - 1) * gap) / n : 0
        return { bw, gap }
    }

    /** 第 i 个点的 x 坐标（相对 SVG 内部） */
    private X(i: number): number {
        const { bw, gap } = this.layout
        return i * (bw + gap) + bw / 2
    }

    /** 值 v 对应的 y 坐标 */
    private Y(v: number): number {
        return this.svgH - (v / TOTAL_MAX) * this.svgH
    }

    /** Catmull-Rom → 三次贝塞尔平滑 */
    private smoothPath(pts: Array<[number, number]>): string {
        // 无数据时返回空串，避免出现 d="M" 这种非法 path 触发浏览器 SVG 解析报错
        if (pts.length === 0) return ''
        if (pts.length < 3) {
            return 'M' + pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L')
        }
        let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(0, i - 1)]
            const p1 = pts[i]
            const p2 = pts[i + 1]
            const p3 = pts[Math.min(pts.length - 1, i + 2)]
            d += `C${(p1[0] + (p2[0] - p0[0]) / 6).toFixed(1)},${(p1[1] + (p2[1] - p0[1]) / 6).toFixed(1)} `
                +  `${(p2[0] - (p3[0] - p1[0]) / 6).toFixed(1)},${(p2[1] - (p3[1] - p1[1]) / 6).toFixed(1)} `
                +  `${p2[0].toFixed(1)},${p2[1].toFixed(1)}`
        }
        return d
    }

    /** 各线条 path */
    private get humanPath(): string {
        return this.smoothPath(this.curData.points.map((d, i) => [this.X(i), this.Y(d.human)]))
    }
    private get botPath(): string {
        return this.smoothPath(this.curData.points.map((d, i) => [this.X(i), this.Y(d.bot)]))
    }
    private get unkPath(): string {
        return this.smoothPath(this.curData.points.map((d, i) => [this.X(i), this.Y(d.unk)]))
    }
    private get totalPath(): string {
        return this.smoothPath(this.curData.points.map((d, i) => [this.X(i), this.Y(d.total)]))
    }
    private get areaPath(): string {
        const n = this.curData.points.length
        if (n === 0) return ''
        return `${this.totalPath} L${this.X(n - 1).toFixed(1)},${this.svgH} L${this.X(0).toFixed(1)},${this.svgH} Z`
    }

    /** 峰值点 */
    private get peakIdx(): number {
        return this.curData.peakIdx
    }
    private get peakVal(): number {
        return this.pointAt(this.peakIdx).total
    }
    private get peakStyle(): { left: string, top: string } {
        if (this.peakIdx < 0 || this.svgW <= 0) return { left: '0%', top: '0' }
        const x = this.X(this.peakIdx)
        return {
            left: (x / this.svgW * 100).toFixed(1) + '%',
            top: this.Y(this.peakVal).toFixed(1) + 'px'
        }
    }
    /** 峰值文案随档位变化（采用后端 peak 字段，文案随桶跨度变化） */
    private get peakText(): string {
        const B = this.curData.bucketH
        const p = this.curData.points[this.curData.peakIdx]
        if (!p) return ''
        const d = new Date(p.ts)
        if (B >= 24) return `峰值 ${this.fmtK(p.total)}/h · ${this.dayLabel(d)}`
        if (B > 1) {
            const e = new Date(d.getTime() + B * HOUR)
            return `峰值 ${this.fmtK(p.total)}/h · ${this.dayLabel(d)} ${this.pad2(d.getHours())}:00–${this.pad2(e.getHours())}:00`
        }
        return `峰值 ${this.fmtK(p.total)}/h · ${this.pad2(d.getHours())}:00`
    }

    /** 末端点（「现在」呼吸点，仅实时档显示） */
    private get lastPt(): { x: number, y: number } {
        const n = this.curData.points.length
        if (n === 0) return { x: 0, y: this.svgH }
        return { x: this.X(n - 1), y: this.Y(this.curData.points[n - 1].total) }
    }
    private get showNowRing(): boolean {
        return this.curData.bucketH <= 1 && this.curData.points.length > 0
    }

    /** hover 状态 */
    private get xLineLeft(): number {
        if (this.hoverIdx < 0) return 0
        return 48 + this.X(this.hoverIdx)
    }
    /** tooltip 标题：随档位格式变化 */
    private get hoverTitle(): string {
        const p = this.pointAt(this.hoverIdx)
        const d = new Date(p.ts)
        const B = this.curData.bucketH
        if (B >= 24) return `${this.dayLabel(d)} 周${WD[d.getDay()]}`
        if (B > 1) {
            const e = new Date(d.getTime() + B * HOUR)
            return `${this.dayLabel(d)} ${this.pad2(d.getHours())}:00–${this.pad2(e.getHours())}:00`
        }
        return `${this.pad2(d.getHours())}:00`
    }
    private get hoverTotal(): number {
        return this.pointAt(this.hoverIdx).total
    }
    private get hoverRows(): Array<{ label: string, value: number, pct: number, color: string }> {
        if (this.hoverIdx < 0) return []
        const d = this.curData.points[this.hoverIdx]
        const total = d.total
        const pc = (v: number) => Math.round(v / total * 100)
        return [
            { label: '真人', value: d.human, pct: pc(d.human), color: 'var(--pdm-acc)' },
            { label: '爬虫', value: d.bot,   pct: pc(d.bot),   color: 'var(--pdm-bot)' },
            { label: '未知', value: d.unk,   pct: pc(d.unk),   color: 'var(--pdm-unk)' }
        ]
    }
    private get hoverDots(): Array<{ x: number, y: number, color: string }> {
        if (this.hoverIdx < 0) return []
        const colors = ['var(--pdm-acc)', 'var(--pdm-bot)', 'var(--pdm-unk)', 'var(--pdm-line-total)']
        const keys: Array<keyof Pick<ITrendPoint, 'human' | 'bot' | 'unk' | 'total'>> = ['human', 'bot', 'unk', 'total']
        const i = this.hoverIdx
        const x = this.X(i)
        return keys.map((k, idx) => ({
            x,
            y: this.Y(this.curData.points[i][k]),
            color: colors[idx]
        }))
    }

    private get tipStyle(): { left: string, top: string } {
        if (this.hoverIdx < 0) return { left: '-9999px', top: '-9999px' }
        const x = this.X(this.hoverIdx) + 48
        const offset = 16
        const tipW = 210
        const areaW = this.svgW + 48 + 2
        let left = x + offset
        if (left + tipW > areaW - 8) {
            left = x - tipW - offset
        }
        return {
            left: left + 'px',
            top: '8px'
        }
    }

    /** X 轴刻度：沿 [0, n-1] 等距取 K 个标签（含首尾），均匀铺满整张图；
     * 24h 档跨天时显式加日期前缀。3d/7d 显示 MM-DD HH:00，30d 仅显示日期。 */
    private get xLabels(): Array<{ pos: number, label: string }> {
        const B = this.curData.bucketH
        const n = this.curData.points.length
        if (n === 0) return []
        // 主标签数 K：按档位定
        const K = B >= 24 ? 6 : B >= 6 ? 7 : 6
        // 等距采样：k=0 → i=0；k=K-1 → i=n-1；中间按比例取整；去重保序
        const seen = new Set<number>()
        const indices: number[] = []
        for (let k = 0; k < K; k++) {
            const i = Math.round(k * (n - 1) / Math.max(1, K - 1))
            if (i < 0 || i >= n) continue
            if (seen.has(i)) continue
            seen.add(i)
            indices.push(i)
        }
        const out: Array<{ pos: number, label: string }> = []
        let prevDay = ''
        indices.forEach(i => {
            const p = this.curData.points[i]
            if (!p) return
            const d = new Date(p.ts)
            const day = this.dayLabel(d)
            let label: string
            if (B >= 24) {
                label = day
            } else if (B > 1) {
                // 多小时桶：日 + 桶起点小时（桶大小在 hint 已声明，X 轴不重复带宽）
                label = `${day} ${this.pad2(d.getHours())}:00`
            } else {
                // 1 小时桶：整点小时；跨天时前缀 MM-DD
                const hh = this.pad2(d.getHours()) + ':00'
                label = (day !== prevDay) ? `${day} ${hh}` : hh
            }
            prevDay = day
            out.push({ pos: (i / (n - 1) * 100), label })
        })
        return out
    }

    /** 鼠标移动 - 吸附最近节点 */
    private onMove(e: MouseEvent) {
        if (this.curData.points.length === 0) return
        const el = this.$refs.chartArea as HTMLElement | undefined
        if (!el) return
        const rect = el.getBoundingClientRect()
        const mx = e.clientX - rect.left - 48
        const { bw, gap } = this.layout
        if (bw <= 0) return
        const i = Math.max(0, Math.min(this.curData.points.length - 1, Math.round(mx / (bw + gap))))
        if (i !== this.hoverIdx) {
            this.hoverIdx = i
        }
    }

    private onLeave() {
        this.hoverIdx = -1
    }

    /** 数字格式 */
    private fmtK(v: number): string {
        return v >= 1000
            ? (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
            : String(v)
    }
    private pad2(n: number): string {
        return String(n).padStart(2, '0')
    }
    private dayLabel(d: Date): string {
        return this.pad2(d.getMonth() + 1) + '-' + this.pad2(d.getDate())
    }
}
